import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ErrorBoundary - Capture les erreurs de rendu React
 * Ignore automatiquement les erreurs provenant d'extensions de navigateur
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  /**
   * Liste des patterns d'erreurs à ignorer (extensions, outils tiers, réseau, CSP)
   */
  private static ignoredErrorPatterns = [
    // Extensions de navigateur
    'chrome-extension://',
    'moz-extension://',
    'safari-extension://',
    'extension://',
    'onboarding',
    'onboarding.js',
    // Erreurs réseau
    'ResizeObserver loop',
    'Non-Error promise rejection',
    'Loading chunk',
    'Failed to fetch dynamically imported module',
    'Network request failed',
    'ERR_CONNECTION_RESET',
    'ERR_NETWORK',
    'ERR_INTERNET_DISCONNECTED',
    'net::ERR_',
    // Erreurs de connexion Chrome
    'Could not establish connection',
    'Receiving end does not exist',
    // Erreurs CSP
    'Content Security Policy',
    'violates the following Content Security Policy',
    // Erreurs de modules
    'Importing a module script failed',
  ];

  /**
   * Vérifie si l'erreur doit être ignorée
   */
  private static shouldIgnoreError(error: Error | null, errorInfo: ErrorInfo | null): boolean {
    const errorString = `${error?.message || ''} ${error?.stack || ''} ${errorInfo?.componentStack || ''}`;
    return this.ignoredErrorPatterns.some(pattern => 
      errorString.toLowerCase().includes(pattern.toLowerCase())
    );
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Ne pas mettre à jour l'état si c'est une erreur à ignorer
    if (ErrorBoundary.shouldIgnoreError(error, null)) {
      console.warn('[WanzoCompta] Erreur ignorée (extension/réseau):', error.message);
      return { hasError: false };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Ignorer les erreurs d'extensions et de réseau
    if (ErrorBoundary.shouldIgnoreError(error, errorInfo)) {
      console.warn('[WanzoCompta] Erreur capturée et ignorée:', error.message);
      // Reset l'état pour permettre à l'app de continuer
      this.setState({ hasError: false, error: null, errorInfo: null });
      return;
    }

    // Logger l'erreur pour le débogage
    console.error('[WanzoCompta] Erreur React capturée:', error);
    console.error('[WanzoCompta] Stack composant:', errorInfo.componentStack);

    this.setState({ errorInfo });

    // Ici vous pourriez envoyer l'erreur à un service de monitoring
    // comme Sentry, LogRocket, etc.
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      // UI de fallback personnalisée
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-center w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full mb-4 mx-auto">
              <svg 
                className="w-6 h-6 text-red-600 dark:text-red-400" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                />
              </svg>
            </div>
            
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white text-center mb-2">
              Oups ! Une erreur s'est produite
            </h1>
            
            <p className="text-gray-600 dark:text-gray-400 text-center mb-6">
              L'application a rencontré un problème inattendu. Veuillez réessayer.
            </p>

            {/* Afficher les détails en mode développement */}
            {import.meta.env.DEV && this.state.error && (
              <details className="mb-4 text-sm">
                <summary className="cursor-pointer text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300">
                  Détails techniques
                </summary>
                <pre className="mt-2 p-3 bg-gray-100 dark:bg-gray-700 rounded text-xs overflow-auto max-h-40 text-red-600 dark:text-red-400">
                  {this.state.error.message}
                  {'\n\n'}
                  {this.state.error.stack}
                </pre>
              </details>
            )}

            <div className="flex gap-3">
              <button
                onClick={this.handleReset}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Réessayer
              </button>
              <button
                onClick={this.handleReload}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Recharger la page
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
